import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetAccountModulesQuery, useGetProjectQuery, useGetVersionsQuery } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useAppSelector } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";

import { useGetScenarioConfiguration } from "./useGetScenarioConfiguration";
import { usePermissionCheck } from "./usePermissionCheck";

interface UseCanICollaborationReturn {
  /**
   * Determine whether the user can create collaboration CTS configurations. Rules:
   * - The "Collaboration CTS" Module must be enabled
   * - The "Collaboration CTS Admin" feature flag must be enabled
   * - The project cannot have more than 1 version if no configuration exists. (reverts to standard Collaboration project)
   */
  canSetupCTS: boolean;
  /**
   * Determine whether the user can run analysis on a collaboration CTS project. Rules:
   * - The "Collaboration CTS" Module must be enabled
   * - Analysis is allowed if either:
   *   - The "Collaboration CTS Admin" feature flag is enabled, or
   *   - A CTS configuration already exists for the project (users can analyze even if they didn't set up CTS themselves)
   */
  canAnalyseCTS: boolean;
  /**
   * Whether the user can carve the BOM in a collaboration CTS project.
   */
  canCarveBOM: boolean;
  /**
   * Whether the query to determine the above is still loading
   */
  isLoading: boolean;
  isDisabled: boolean;
}
export const useCanICollaboration = (): UseCanICollaborationReturn => {
  const projectId = useAppSelector((state) => state.project.projectId);
  const { data: project, isLoading: isLoadingProject } = useGetProjectQuery(projectId ?? skipToken);
  const { data: versions, isLoading: isLoadingVersions } = useGetVersionsQuery(projectId ?? skipToken);
  const { configuration, isLoading: isLoadingCollaboration } = useGetScenarioConfiguration({ projectId });

  // Note that currently we are using the "Collaboration CTS Admin" feature flag to gate both setup and analysis of Collaboration CTS.
  // This is because we want to limit configuration creation to Biarri users only.
  // FIXME: Once modules can be user specific rather than account specific, we should use modules instead of feature flags.
  const { value: collaborationCTSAdminFlagEnabled, loading: isLoadingFeatureFlags } = useFeatureFlag("collaboration_cts_admin");
  const { data: accountModules, isLoading: isLoadingAccountModules } = useGetAccountModulesQuery(project?.Account.ID ?? skipToken);
  const hasCollaborationModule = accountModuleCheck(accountModules, Actions.COLLABORATION_CTS);
  const hasCollaborationBomCarveModule = accountModuleCheck(accountModules, Actions.COLLABORATION_CTS_BOM_CARVE);
  const isDisabled = !usePermissionCheck(Actions.PROJECT_EDIT, project?.Permission?.Level);
  const isLoading = isLoadingProject || isLoadingFeatureFlags || isLoadingCollaboration || isLoadingVersions || isLoadingAccountModules;

  return useMemo(() => {
    // While loading we disable all CTS features until we can determine whether the user has access or not
    if (isLoading) {
      return {
        canSetupCTS: false,
        canAnalyseCTS: false,
        canCarveBOM: false,
        isLoading,
        isDisabled,
      };
    }

    // Feature not available without the module or the feature flag enabled.
    if (!hasCollaborationModule) {
      return {
        canSetupCTS: false,
        canAnalyseCTS: false,
        canCarveBOM: false,
        isLoading,
        isDisabled,
      };
    }

    // If the project has multiple versions and a configuration doesn't exist, we assume the user is
    // using the standard versioning & therefore we cannot support scenarios.
    if (!!versions && versions.ids.length > 1 && !configuration) {
      return {
        canSetupCTS: false,
        canAnalyseCTS: false,
        canCarveBOM: false,

        isLoading: false,
        isDisabled,
      };
    }

    return {
      isLoading,
      canSetupCTS: collaborationCTSAdminFlagEnabled,
      canAnalyseCTS: collaborationCTSAdminFlagEnabled || !!configuration, // Users can analyze if they have the feature flag or if a configuration already exists.
      canCarveBOM: collaborationCTSAdminFlagEnabled && hasCollaborationBomCarveModule,
      isDisabled,
    };
  }, [isLoading, hasCollaborationModule, versions, configuration, collaborationCTSAdminFlagEnabled, hasCollaborationBomCarveModule, isDisabled]);
};
